/**
 * TemplateProgramsTab — lists rolled-out programs derived from the template,
 * union of cadence-fired (cadence_rule_id != null) and manual rollouts.
 *
 * Migration 114. The endpoint queried depends on the template kind:
 *   SOP                       -> /api/v1/prm/projects?source_type_id=&source_record_id=
 *   RISK_ASSESSMENT_TEMPLATE  -> /api/v1/rsk/assessment-programs?template_id=
 *   CONTROL_TEST_TEMPLATE     -> /api/v1/ctr/test-programs?template_id=
 *
 * Read-only listing for now — manual "Roll out" still happens via the
 * existing per-module rollout flows.
 */
import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../../services/apiService';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import extractApiError from '../../utils/apiError';

interface Props {
  sourceTypeId: number;
  sourceRecordId: number;
  sourceTypeCode: string;
}

interface ProgramRow {
  id: number;
  code?: string;
  name?: string;
  pm?: string | number;
  geography?: string | number;
  period?: string;
  source: 'cadence' | 'manual';
  cadence_rule_id?: number;
  start?: string;
  finish?: string;
  status?: string;
}

const TemplateProgramsTab: React.FC<Props> = ({ sourceTypeId, sourceRecordId, sourceTypeCode }) => {
  const [rows, setRows] = useState<ProgramRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let mapped: ProgramRow[] = [];
      if (sourceTypeCode === 'SOP') {
        const res = await apiClient.get('/api/v1/gov/sop-programs', {
          params: { sop_id: sourceRecordId, limit: 100 },
        });
        const list: any[] = res.data?.data ?? res.data ?? [];
        mapped = list.map((p: any) => ({
          id: p.id,
          code: undefined,
          name: p.program_name,
          pm: p.maker_name ?? p.maker_id ?? '—',
          geography: p.location_name ?? p.location_id ?? '—',
          start: p.start_date,
          finish: p.end_date,
          status: p.status,
          source: p.cadence_rule_id ? 'cadence' : 'manual',
          cadence_rule_id: p.cadence_rule_id,
        }));
      } else if (sourceTypeCode === 'RISK_ASSESSMENT_TEMPLATE') {
        const res = await apiClient.get('/api/v1/rsk/assessment-programs', {
          params: { template_id: sourceRecordId },
        });
        const list: any[] = res.data?.data ?? res.data ?? [];
        mapped = list.map((p: any) => ({
          id: p.id,
          code: p.program_code,
          name: p.program_name,
          pm: p.maker_name ?? p.maker_id ?? '—',
          geography: p.location_name ?? p.location_id ?? '—',
          start: p.start_date,
          finish: p.end_date,
          status: p.status,
          source: p.cadence_rule_id ? 'cadence' : 'manual',
          cadence_rule_id: p.cadence_rule_id,
        }));
      } else if (sourceTypeCode === 'CONTROL_TEST_TEMPLATE') {
        const res = await apiClient.get('/api/v1/ctr/test-programs', {
          params: { template_id: sourceRecordId },
        });
        const list: any[] = res.data?.data ?? res.data ?? [];
        mapped = list.map((p: any) => ({
          id: p.id,
          code: undefined,
          name: p.test_name,
          pm: p.maker_name ?? p.maker_id ?? '—',
          geography: p.location_name ?? p.location_id ?? '—',
          start: p.start_date,
          finish: p.end_date,
          status: p.status,
          source: p.cadence_rule_id ? 'cadence' : 'manual',
          cadence_rule_id: p.cadence_rule_id,
        }));
      }
      setRows(mapped);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs.') });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [sourceTypeId, sourceRecordId, sourceTypeCode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pageSize, rows.length]);

  const totalRecords = rows.length;
  const paginatedRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="flex flex-col h-full">
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {rows.length} total — {rows.filter(r => r.source === 'cadence').length} cadence-fired
          </span>
        </div>
      </div>

      <div className="table-list flex-1 min-h-0">
        <div className="table-list-scroll">
          <table className="table-list-table">
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <th className="table-list-th">Code</th>
                <th className="table-list-th">Name</th>
                <th className="table-list-th">Owner</th>
                <th className="table-list-th">Geography</th>
                <th className="table-list-th">Source</th>
                <th className="table-list-th">Start</th>
                <th className="table-list-th">Finish</th>
                <th className="table-list-th table-list-cell--status">Status</th>
              </tr>
            </thead>
            <tbody aria-busy={loading} aria-live="polite">
              {loading && (
                <Skeleton.TableRows
                  cols={[
                    { width: '3xs', align: 'center' },
                    '2xs',
                    'md',
                    'sm',
                    'sm',
                    'pill',
                    'xs',
                    'xs',
                    'pill',
                  ]}
                />
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="table-list-cell">
                    <NoDataFound
                      title="No Programs Rolled Out"
                      message="No programs have been rolled out from this template yet."
                    />
                  </td>
                </tr>
              )}
              {!loading && paginatedRows.map((r, idx) => (
                <tr key={r.id} className="table-list-row" data-testid={`ROW_template_program_${r.id}`}>
                  <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                  <td className="table-list-cell"><span className="cell-code">{r.code || '—'}</span></td>
                  <td className="table-list-cell">
                    <span className="table-list-cell-title">{r.name || '—'}</span>
                  </td>
                  <td className="table-list-cell">{r.pm ?? '—'}</td>
                  <td className="table-list-cell">{r.geography ?? '—'}</td>
                  <td className="table-list-cell">
                    {r.source === 'cadence'
                      ? <span className="run-source s-scheduled">cadence #{r.cadence_rule_id}</span>
                      : <span className="run-source s-manual">manual</span>}
                  </td>
                  <td className="table-list-cell">{r.start || '—'}</td>
                  <td className="table-list-cell">{r.finish || '—'}</td>
                  <td className="table-list-cell">{r.status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          recordLabel="programs"
        />
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default TemplateProgramsTab;
